// /src/pages/LoginPage.jsx
// VERSÃO 6.0 GOLD - PORTÃO DE ELITE COM PROTOCOLO DE QUARENTENA

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion'; // Upgrade para animações fluidas
import { FiClock, FiX } from 'react-icons/fi'; // Ícone da ampulheta
import apiClient from '../api/apiClient';

const allowRegister = (import.meta.env.VITE_ALLOW_REGISTRATION || 'true').toLowerCase() === 'true';

export default function LoginPage({ onLoginSuccess }) {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [isMasterLogin, setIsMasterLogin] = useState(false); 
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showQuarantine, setShowQuarantine] = useState(false); // 🔍 Gatilho do Overlay
  const navigate = useNavigate();

  const handleChange = (e) => {
    setCredentials(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleLogoClick = () => {
    setIsMasterLogin(prev => !prev);
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const loginPath = isMasterLogin ? '/master-auth/login' : '/auth/login';

    try {
      const response = await apiClient.post(loginPath, credentials);
      const { token, username, id, role } = response.data;

      if (token) {
        localStorage.setItem('token', token);
        localStorage.setItem('authType', isMasterLogin ? 'master' : 'user');
        if (username) localStorage.setItem('loggedUserName', username);
        if (id) localStorage.setItem('loggedUserId', id); 
        if (role) localStorage.setItem('loggedUserRole', role);

        if (typeof onLoginSuccess === 'function') {
          onLoginSuccess(token);
        }
        navigate('/dashboard');
      }

    } catch (err) {
      // 🛡️ INTERCEPTAÇÃO DE QUARENTENA (STATUS 403)
      if (err.status === 403 && err.raw?.message?.includes('Quarentena')) {
        setShowQuarantine(true);
      } else {
        const errorMessage = err.message || 'Falha na autenticação. Verifique o perímetro.';
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 transition-all duration-500"
         style={{ backgroundColor: 'var(--bg-main)' }}>
      
      {/* 🛡️ CARD DE LOGIN ELITE */}
      <div className="w-full max-w-md p-10 space-y-8 backdrop-blur-xl border rounded-[2.5rem] shadow-[0_50px_100px_rgba(0,0,0,0.4)] transition-all duration-500 relative"
           style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
        
        <div className="flex justify-center">
          <button onClick={handleLogoClick} className="focus:outline-none group relative">
            <div className={`absolute inset-0 rounded-full blur-2xl transition-all duration-700 ${isMasterLogin ? 'opacity-60 scale-125' : 'opacity-0'}`}
                 style={{ backgroundColor: 'var(--accent-color)' }}></div>
            <img 
              src="/logo.png" 
              alt="MOS" 
              className={`w-28 h-28 rounded-full border-2 relative z-10 transition-all duration-500 ${isMasterLogin ? 'scale-110 shadow-2xl' : ''}`} 
              style={{ borderColor: isMasterLogin ? 'var(--accent-color)' : 'var(--border-color)' }}
            />
          </button>
        </div>

        <div className="text-center space-y-2">
          <h1 className="text-4xl font-black tracking-tighter uppercase"
              style={{ color: 'var(--text-primary)' }}>
            MOS <span style={{ color: 'var(--accent-color)' }}>Elite</span>
          </h1>
          <p className="text-[10px] font-black uppercase tracking-[0.4em] opacity-40"
              style={{ color: 'var(--text-primary)' }}>
            {isMasterLogin ? '🛡️ Painel de Operações' : '🏢 Painel de Operações'}
          </p>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase tracking-widest opacity-50 ml-2"
                   style={{ color: 'var(--text-primary)' }}>Identificador</label>
            <input 
              type="text" name="username" value={credentials.username} onChange={handleChange} 
              className="w-full px-5 py-4 rounded-2xl outline-none border transition-all"
              style={{ backgroundColor: 'var(--bg-main)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              placeholder="Username" required disabled={isLoading} 
            />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase tracking-widest opacity-50 ml-2"
                   style={{ color: 'var(--text-primary)' }}>Chave de Acesso</label>
            <input 
              type="password" name="password" value={credentials.password} onChange={handleChange} 
              className="w-full px-5 py-4 rounded-2xl outline-none border transition-all"
              style={{ backgroundColor: 'var(--bg-main)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              placeholder="••••••••" required disabled={isLoading} 
            />
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/50 text-red-500 text-[10px] font-black uppercase tracking-widest rounded-xl p-4 text-center">
              {error}
            </div>
          )}
          
          <button 
            type="submit" 
            disabled={isLoading} 
            className="w-full py-5 font-black uppercase tracking-[0.3em] text-[11px] text-white rounded-2xl transition-all shadow-xl hover:brightness-110 active:scale-95 disabled:opacity-50"
            style={{ backgroundColor: 'var(--accent-color)' }}
          >
            {isLoading ? 'Validando Credenciais...' : 'Entrar no Sistema'}
          </button>
        </form>

        {allowRegister && !isMasterLogin && (
          <div className="text-center pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
            <Link to="/register" 
                  className="text-[10px] font-black uppercase tracking-widest transition-all hover:opacity-100 opacity-40"
                  style={{ color: 'var(--text-primary)' }}>
              Solicitar Registro de Unidade
            </Link>
          </div>
        )}

        {/* ⏳ OVERLAY DE QUARENTENA (POPUP TÁTICO) */}
        <AnimatePresence>
          {showQuarantine && (
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 z-50 rounded-[2.5rem] flex flex-col items-center justify-center p-8 text-center backdrop-blur-2xl border-2 border-yellow-500/30"
              style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
            >
              <button onClick={() => setShowQuarantine(false)} className="absolute top-6 right-6 text-white/30 hover:text-white transition-all">
                <FiX size={20} />
              </button>
              
              <motion.div 
                initial={{ scale: 0.5, rotate: -180 }} animate={{ scale: 1, rotate: 0 }}
                className="w-20 h-20 bg-yellow-500/10 rounded-full flex items-center justify-center mb-6 border border-yellow-500/50"
              >
                <FiClock className="text-yellow-500 animate-pulse" size={40} />
              </motion.div>

              <h2 className="text-xl font-black uppercase tracking-tighter text-white mb-2">Acesso Retido</h2>
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-yellow-500 opacity-80 mb-6">Status: Em Analise</p>
              
              <p className="text-[11px] leading-relaxed text-white/60 font-medium uppercase tracking-widest px-4">
                Seu cadastro foi recebido, mas sua identidade ainda está sob análise <span className="text-yellow-500 font-black">MOS</span>. 
                Tente novamente em breve ou fale com seu Gestor.
              </p>

              <button 
                onClick={() => setShowQuarantine(false)}
                className="mt-10 px-8 py-3 bg-white/5 border border-white/10 rounded-xl text-[9px] font-black uppercase tracking-[0.3em] text-white hover:bg-white/10 transition-all"
              >
                Entendido
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <p className="mt-10 text-[8px] font-bold uppercase tracking-[0.5em] opacity-20"
         style={{ color: 'var(--text-primary)' }}>
        SniperGuard Security Protocol v2.6
      </p>
    </div>
  );
}